import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";

const FAQS = [
  {
    q: "What does Robert specialize in?",
    a: "Inventory control and material requirements planning (MRP) — guiding small-to-mid-size manufacturers and distributors from manual spreadsheets and static BOMs to automated, data-driven planning workflows.",
  },
  {
    q: "Who does he work with?",
    a: "Small-to-mid-size manufacturing and distribution operations that are outgrowing manual tracking and legacy accounting-only workflows.",
  },
  {
    q: "What kind of engagements are available?",
    a: "Contract projects, consulting and advisory work, interim or fractional roles, and full-time opportunities.",
  },
  {
    q: "How do I start a conversation?",
    a: "Submit the contact form with your name, company, email, project or role scope, and a short message — inquiries go directly to Robert's inbox.",
  },
];

export default function Faq() {
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" aria-label="Common questions" className="relative py-24 sm:py-28">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#00F0FF]">
            05 — Common Questions
          </p>
          <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl text-white leading-[1.1]">
            Straight answers, before you even ask.
          </h2>
        </motion.div>

        <div className="mt-10 divide-y divide-white/[0.07] border-y border-white/[0.07]">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <motion.div
                key={f.q}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
              >
                <button
                  data-testid={`faq-item-${i}`}
                  aria-expanded={isOpen}
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between gap-6 text-left py-6 group"
                >
                  <span className={`font-display font-semibold text-lg transition-colors duration-300 ${isOpen ? "text-[#00F0FF]" : "text-white group-hover:text-[#00F0FF]"}`}>
                    {f.q}
                  </span>
                  <motion.span animate={{ rotate: isOpen ? 45 : 0 }} transition={{ duration: 0.25 }} className="shrink-0 text-[#6B7280]">
                    <Plus size={20} />
                  </motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                      className="overflow-hidden"
                    >
                      <p data-testid={`faq-answer-${i}`} className="pb-6 pr-10 text-[15px] text-[#9CA3AF] leading-relaxed">
                        {f.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
